import { CachedPdf, CacheStats, PdfCacheOptions } from "@shared/types";
import { useCallback, useRef } from "react";

const DEFAULT_OPTIONS: Required<PdfCacheOptions> = {
  maxAge: 60 * 60 * 1000, // 1 hour
  maxSize: 20, // Reduced for large datasets to prevent memory issues
};

export const usePdfCache = (options: PdfCacheOptions = {}) => {
  const cacheRef = useRef<Map<string, CachedPdf>>(new Map());
  const { maxAge, maxSize } = { ...DEFAULT_OPTIONS, ...options };

  // Private helper to safely revoke blob URLs
  const revokeBlobUrl = useCallback((url: string) => {
    if (url.startsWith("blob:")) {
      URL.revokeObjectURL(url);
    }
  }, []);

  const getCachedPdf = useCallback(
    (key: string): string | null => {
      // Clean up all expired entries before proceeding
      const now = Date.now();
      for (const [cacheKey, cachedEntry] of cacheRef.current.entries()) {
        if (now - cachedEntry.timestamp > maxAge) {
          revokeBlobUrl(cachedEntry.url);
          cacheRef.current.delete(cacheKey);
        }
      }

      const cached = cacheRef.current.get(key);
      if (!cached) return null;
      // Update access tracking
      cached.accessCount++;
      cached.lastAccessed = Date.now();

      return cached.url;
    },
    [maxAge, revokeBlobUrl]
  );

  const cleanupExpiredEntries = useCallback(() => {
    const now = Date.now();
    for (const [cacheKey, cached] of cacheRef.current.entries()) {
      if (now - cached.timestamp > maxAge) {
        // Clean up blob URL to prevent memory leaks
        revokeBlobUrl(cached.url);
        cacheRef.current.delete(cacheKey);
      }
    }
  }, [maxAge, revokeBlobUrl]);

  const evictLeastRecentlyUsed = useCallback(() => {
    let oldestKey = "";
    let oldestTime = Date.now();

    for (const [cacheKey, cached] of cacheRef.current.entries()) {
      if (cached.lastAccessed < oldestTime) {
        oldestTime = cached.lastAccessed;
        oldestKey = cacheKey;
      }
    }

    if (oldestKey) {
      const oldCached = cacheRef.current.get(oldestKey);
      // Clean up blob URL to prevent memory leaks
      if (oldCached) {
        revokeBlobUrl(oldCached.url);
      }
      cacheRef.current.delete(oldestKey);
    }
  }, [revokeBlobUrl]);

  const setCachedPdf = useCallback(
    (key: string, url: string) => {
      // Clean up expired entries first
      cleanupExpiredEntries();

      // If cache is at max size, use LRU eviction
      if (cacheRef.current.size >= maxSize) {
        evictLeastRecentlyUsed();
      }

      const now = Date.now();
      cacheRef.current.set(key, {
        url,
        timestamp: now,
        accessCount: 0,
        lastAccessed: now,
      });
    },
    [maxSize, cleanupExpiredEntries, evictLeastRecentlyUsed]
  );

  const clearCache = useCallback(() => {
    // Clean up all blob URLs before clearing cache
    for (const cached of cacheRef.current.values()) {
      revokeBlobUrl(cached.url);
    }
    cacheRef.current.clear();
  }, [revokeBlobUrl]);

  const getCacheStats = useCallback((): CacheStats => {
    const now = Date.now();
    let validEntries = 0;
    let expiredEntries = 0;
    let totalAccessCount = 0;

    for (const cached of cacheRef.current.values()) {
      if (now - cached.timestamp > maxAge) {
        expiredEntries++;
      } else {
        validEntries++;
        totalAccessCount += cached.accessCount;
      }
    }

    return {
      total: cacheRef.current.size,
      valid: validEntries,
      expired: expiredEntries,
      totalAccessCount,
      averageAccessCount: validEntries > 0 ? totalAccessCount / validEntries : 0,
    };
  }, [maxAge]);

  const forceCleanup = useCallback(() => {
    const now = Date.now();
    for (const [cacheKey, cached] of cacheRef.current.entries()) {
      if (now - cached.timestamp > maxAge) {
        // Clean up blob URL to prevent memory leaks
        revokeBlobUrl(cached.url);
        cacheRef.current.delete(cacheKey);
      }
    }
  }, [maxAge, revokeBlobUrl]);

  return {
    getCachedPdf,
    setCachedPdf,
    clearCache,
    getCacheStats,
    forceCleanup,
  };
};
